import React, { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IoTrash } from 'react-icons/io5';
import { getAgencyProducts } from '~/api/agencies';
import { useGetEtaiement } from '~/hooks/etaiements';
import {
  InputNumber,
  InputText,
  StyledSelect,
} from '~/lib/HooksFormFields';
import PreviewField from '~/components/PreviewField';
import type { IAgencyProduct } from '~/types/agencies';
import type { IOption } from '~/types/options';
import { digitFormatter } from '~/utils';
import {
  PRESTATION_PRICE_UNIT_OPTIONS,
  PRESTATION_SUPPORT_OPTIONS,
  computePrestationLineTotal,
  emptyPrestationRow,
  getPrestationUnitPrice,
  isPrestationFlatPriceUnit,
  type IEtaiementPrestationRow,
} from '~/utils/etaiement-prestations';
import styles from './prestations-form.module.scss';

const buildProductOptions = (agencyProducts: IAgencyProduct[]): IOption[] => (
  (agencyProducts || [])
    .map((ap: IAgencyProduct) => ({
      label: ap.product?.title || '',
      value: String(ap.productId),
    }))
    .sort((a: IOption, b: IOption) => a.label.localeCompare(b.label, 'fr'))
);

const PrestationsForm = ({ isPreview }: { isPreview: boolean }) => {
  const { id } = useParams();
  const { control, watch, setValue } = useFormContext();
  const { data: etaiement } = useGetEtaiement(id || '');

  const agencyId = typeof etaiement?.agency === 'object'
    ? etaiement?.agency?._id
    : etaiement?.agency;

  const { data: agencyProducts = [] } = useQuery({
    queryKey: ['agency-products', { id: agencyId }],
    queryFn: async () => getAgencyProducts({ id: agencyId || '' }),
    enabled: Boolean(agencyId),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prestations',
  });

  const prestations = watch('prestations') || [];

  const productOptions = useMemo(
    () => buildProductOptions(agencyProducts),
    [agencyProducts],
  );

  const findAgencyProduct = useCallback((productId: string) => (
    (agencyProducts || []).find(
      (ap: IAgencyProduct) => String(ap.productId) === String(productId),
    )
  ), [agencyProducts]);

  const updateRowPricing = useCallback((index: number) => {
    const row = watch(`prestations.${index}`);
    const agencyProduct = row?.productId
      ? findAgencyProduct(String(row.productId))
      : undefined;
    const unitPrice = getPrestationUnitPrice(agencyProduct, row?.priceUnit);
    if (isPrestationFlatPriceUnit(row?.priceUnit)) {
      setValue(`prestations.${index}.quantity`, null, { shouldDirty: true });
    }
    const lineTotal = computePrestationLineTotal(
      unitPrice,
      row?.quantity,
      row?.priceUnit,
    );
    setValue(`prestations.${index}.unitPrice`, unitPrice, { shouldDirty: true });
    setValue(`prestations.${index}.lineTotal`, lineTotal, { shouldDirty: true });
  }, [findAgencyProduct, setValue, watch]);

  const handlePriceUnitChange = (index: number, opt: IOption | null) => {
    const priceUnit = String(opt?.value ?? '');
    setValue(`prestations.${index}.priceUnit`, priceUnit, { shouldDirty: true });
    if (isPrestationFlatPriceUnit(priceUnit)) {
      setValue(`prestations.${index}.quantity`, null, { shouldDirty: true });
    }
    updateRowPricing(index);
  };

  const handleProductChange = (index: number, opt: IOption | null) => {
    const agencyProduct = opt?.value ? findAgencyProduct(String(opt.value)) : undefined;
    setValue(`prestations.${index}.productId`, opt?.value ?? '', { shouldDirty: true });
    setValue(
      `prestations.${index}.productType`,
      agencyProduct?.product?.productType ?? '',
      { shouldDirty: true },
    );
    if (!opt?.value) {
      setValue(`prestations.${index}.priceUnit`, '', { shouldDirty: true });
      setValue(`prestations.${index}.unitPrice`, null, { shouldDirty: true });
      setValue(`prestations.${index}.lineTotal`, null, { shouldDirty: true });
    }
    updateRowPricing(index);
  };

  const getRowProductType = (row: IEtaiementPrestationRow): string => {
    if (row.productType?.trim()) return row.productType.trim();
    const agencyProduct = row.productId
      ? findAgencyProduct(String(row.productId))
      : undefined;
    return agencyProduct?.product?.productType?.trim() || '';
  };

  const renderProductTypeSubline = (row: IEtaiementPrestationRow) => {
    const productType = getRowProductType(row);
    if (!productType) return null;
    return <span className={styles.productTypeSubline}>{productType}</span>;
  };

  const getPriceUnitLabel = (priceUnit: string) => (
    PRESTATION_PRICE_UNIT_OPTIONS.find((o) => o.value === priceUnit)?.label || '---'
  );

  const renderUnitPriceHint = (row: IEtaiementPrestationRow) => {
    if (row.unitPrice == null || Number(row.unitPrice) <= 0) return null;
    return (
      <span className={styles.cellHint}>
        {digitFormatter?.format(Number(row.unitPrice))} €
      </span>
    );
  };

  const renderLineTotalHint = (row: IEtaiementPrestationRow) => {
    if (row.lineTotal == null || Number(row.lineTotal) <= 0) return null;
    return (
      <span className={`${styles.cellHint} ${styles.lineTotalHint}`}>
        {digitFormatter?.format(Number(row.lineTotal))} €
      </span>
    );
  };

  const renderQuantityCell = (index: number, row: IEtaiementPrestationRow, isFlat: boolean) => {
    if (isFlat) {
      return <span className={styles.naCell}>—</span>;
    }
    if (isPreview) {
      return (
        <PreviewField
          label=''
          value={row.quantity != null ? String(row.quantity) : '---'}
        />
      );
    }
    return (
      <InputNumber
        name={`prestations.${index}.quantity`}
        control={control}
        allowFloat
        min='0'
        handleChange={() => updateRowPricing(index)}
      />
    );
  };

  if (!agencyId) {
    return (
      <div id="prestations" className={styles.prestations}>
        <h4>Prestations</h4>
        <p className={styles.emptyHint}>
          Associez une agence au devis pour renseigner les prestations.
        </p>
      </div>
    );
  }

  return (
    <div id="prestations" className={styles.prestations}>
      <h4>Prestations</h4>
      {fields.length === 0 && !isPreview && (
        <p className={styles.emptyHint}>Aucune prestation. Ajoutez une ligne pour commencer.</p>
      )}
      {fields.length > 0 && (
        <div className={styles.tableWrapper}>
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th className={styles.support}>Appui</th>
                <th className={styles.heightCell}>Hauteur</th>
                <th className={styles.levelCell}>Niveau</th>
                <th>Type de prix</th>
                <th className={styles.quantityCell}>Quantité</th>
                {!isPreview && <th className={styles.actionsCell} />}
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const row = prestations[index] || {};
                const isFlat = isPrestationFlatPriceUnit(row.priceUnit);
                return (
                  <tr key={field.id}>
                    <td className={styles.productCell}>
                      {isPreview
                        ? (
                          <>
                            <PreviewField
                              label=''
                              value={
                                findAgencyProduct(String(row.productId || ''))?.product?.title
                                || '---'
                              }
                            />
                            {renderProductTypeSubline(row)}
                          </>
                        )
                        : (
                          <>
                            <StyledSelect
                              name={`prestations.${index}.productId`}
                              control={control}
                              options={productOptions}
                              placeholder='Produit'
                              isSearchable
                              menuInPortal
                              handleChange={(opt) => handleProductChange(index, opt)}
                            />
                            {renderProductTypeSubline(row)}
                          </>
                        )
                      }
                    </td>
                    <td className={styles.support}>
                      {isPreview
                        ? <PreviewField label='' value={row.support || '---'} />
                        : <StyledSelect
                          name={`prestations.${index}.support`}
                          control={control}
                          options={[...PRESTATION_SUPPORT_OPTIONS]}
                          placeholder='Appui'
                          isClearable
                          menuInPortal
                        />
                      }
                    </td>
                    <td className={styles.heightCell}>
                      {isPreview
                        ? <PreviewField
                          label=''
                          value={row.height != null && row.height !== '' ? `${row.height}` : '---'}
                        />
                        : <InputNumber
                          name={`prestations.${index}.height`}
                          control={control}
                          allowFloat
                          min='0'
                          handleChange={() => updateRowPricing(index)}
                        />
                      }
                    </td>
                    <td className={styles.levelCell}>
                      {isPreview
                        ? <PreviewField label='' value={row.level || '---'} />
                        : <InputText
                          name={`prestations.${index}.level`}
                          control={control}
                          placeholder='Niveau'
                        />
                      }
                    </td>
                    <td className={styles.priceUnitCell}>
                      {isPreview
                        ? (
                          <>
                            <PreviewField label='' value={getPriceUnitLabel(row.priceUnit)} />
                            {renderUnitPriceHint(row)}
                          </>
                        )
                        : (
                          <>
                            <StyledSelect
                              name={`prestations.${index}.priceUnit`}
                              control={control}
                              options={[...PRESTATION_PRICE_UNIT_OPTIONS]}
                              placeholder='Type de prix'
                              isDisabled={!row.productId}
                              menuInPortal
                              handleChange={(opt) => handlePriceUnitChange(index, opt)}
                            />
                            {renderUnitPriceHint(row)}
                          </>
                        )
                      }
                    </td>
                    <td className={styles.quantityCell}>
                      {renderQuantityCell(index, row, isFlat)}
                      {renderLineTotalHint(row)}
                    </td>
                    {!isPreview && (
                      <td className={styles.actionsCell}>
                        <button
                          type="button"
                          className={styles.removeRow}
                          onClick={() => remove(index)}
                          aria-label="Supprimer la ligne"
                        >
                          {IoTrash({ size: 18 })}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!isPreview && (
        <button
          type="button"
          className={styles.addRow}
          onClick={() => append(emptyPrestationRow())}
        >
          Ajouter une ligne
        </button>
      )}
    </div>
  );
};

export default PrestationsForm;
